import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Same color language as the digital-score tone elsewhere: green = strong,
// red = weak. Here it means "how visible you are at this point on the map".
const rankColor = (rank) => {
  if (rank == null) return "hsl(0,72%,50%)";       // not visible in results at all
  if (rank <= 3) return "hsl(142,55%,42%)";          // in the local pack top 3
  if (rank <= 10) return "hsl(45,90%,48%)";
  if (rank <= 20) return "hsl(24,90%,52%)";
  return "hsl(0,72%,50%)";
};

const rankLabel = (rank) => (rank == null ? "–" : rank > 20 ? "20+" : String(rank));

// Legend/tooltip chrome only — kept local rather than importing ReportPage's
// dictionary, since this is the only string content in this component.
const STRINGS = {
  de: {
    rank: (r) => `Platz ${r}`,
    notInTop: "Nicht in den Top-Ergebnissen",
    top3: "Top 3",
    rank4to10: "Platz 4–10",
    rank11to20: "Platz 11–20",
    notVisible: "Nicht sichtbar",
    visibleAt: (visible, total) => `An ${visible} von ${total} Punkten sichtbar`,
    whoRanksHere: "Wer hier rangiert",
  },
  en: {
    rank: (r) => `Rank ${r}`,
    notInTop: "Not in the top results",
    top3: "Top 3",
    rank4to10: "Rank 4–10",
    rank11to20: "Rank 11–20",
    notVisible: "Not visible",
    visibleAt: (visible, total) => `Visible at ${visible} of ${total} points`,
    whoRanksHere: "Who ranks here",
  },
};

// Leaflet's built-in CircleMarker is an SVG path — it can't contain text.
// A divIcon Marker renders arbitrary HTML instead, which is how the rank
// number gets printed inside the colored circle itself, not just on hover.
const rankIcon = (rank) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:30px;height:30px;border-radius:9999px;
      background:${rankColor(rank)};border:2px solid #fff;
      box-shadow:0 1px 3px rgba(0,0,0,.35);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:11px;font-weight:700;font-family:inherit;
    ">${rankLabel(rank)}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

function FitToGrid({ points }) {
  const map = useMap();
  // Effects below re-read the latest points without re-subscribing the
  // ResizeObserver (which only needs to run once per map instance).
  const pointsRef = useRef(points);
  pointsRef.current = points;

  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [24, 24] });
  }, [points, map]);

  // ReportPage.js keeps every report tab mounted at all times and only
  // toggles Tailwind's `hidden` class (or reveals them all at once via
  // `print:block` for the PDF export) — it never unmounts a tab. Leaflet
  // sizes its tile grid and marker positions from the container's pixel
  // dimensions at the moment it mounts; a `display:none` container reports
  // 0x0, so a map that first mounts inside a hidden tab (the default here,
  // since "overview" is the active tab) ends up with a degenerate layout.
  // Leaflet has no built-in way to notice the container becoming visible
  // later, whether that happens via a tab click (a JS class toggle) or via
  // print (a CSS media query) — so watch the container directly and, when
  // it goes from invisible to a real size, tell Leaflet to re-measure
  // (`invalidateSize`) and redo the fit so markers land in the right place.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const container = map.getContainer();
    let wasVisible = container.offsetWidth > 0 && container.offsetHeight > 0;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const isVisible = width > 0 && height > 0;
      if (isVisible) {
        // Harmless (and cheap) to call on every legitimate resize too, not
        // just the invisible->visible transition.
        map.invalidateSize();
        if (!wasVisible && pointsRef.current.length) {
          map.fitBounds(pointsRef.current.map((p) => [p.lat, p.lng]), { padding: [24, 24] });
        }
      }
      wasVisible = isVisible;
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
}

export const GridRankMap = ({ gridRank, lang }) => {
  const t = STRINGS[lang] || STRINGS.de;
  const points = gridRank?.points || [];
  const icons = useMemo(() => points.map((p) => rankIcon(p.rank)), [points]);
  if (!points.length) return null;

  const visible = points.filter((p) => p.rank != null).length;

  return (
    <div className="space-y-2">
      <div
        className="overflow-hidden rounded-xl border"
        style={{ height: "360px" }}
        data-testid="grid-rank-map"
      >
        <MapContainer
          center={[gridRank.center.lat, gridRank.center.lng]}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitToGrid points={points} />
          {points.map((p, i) => (
            <Marker key={i} position={[p.lat, p.lng]} icon={icons[i]}>
              <Tooltip direction="top" offset={[0, -16]}>
                <div>
                  <div className="font-medium">{p.rank != null ? t.rank(p.rank) : t.notInTop}</div>
                  {p.top3?.length > 0 && (
                    <div className="mt-1 border-t pt-1 text-[11px] leading-tight">
                      <div className="font-medium text-muted-foreground">{t.whoRanksHere}</div>
                      <ol className="mt-0.5 list-decimal pl-3.5">
                        {p.top3.map((c, ci) => (
                          <li key={ci} className="truncate max-w-[180px]">{c.name}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: rankColor(2) }} /> {t.top3}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: rankColor(7) }} /> {t.rank4to10}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: rankColor(15) }} /> {t.rank11to20}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: rankColor(null) }} /> {t.notVisible}
        </span>
        <span className="ml-auto">
          {t.visibleAt(visible, points.length)}
        </span>
      </div>
    </div>
  );
};
